import { DateTime } from 'luxon';
import * as clipboard from 'smg/clipboard';
import { BuilderDesignByOrderLineItem, DeleteBuilderDesign, RestoreBuilderDesign } from 'smg/customer-api/builder-designs';
import * as session from 'smg/customer-api/session';
import { LoadPreviousDesigns, PreviousDesignEntry } from 'smg/customizer/previous-designs';
import Modal from 'smg/modal';
import { Picture } from 'smg/picture';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { Relref } from '../hugo';

// Heavy users page at this many cards behind a "Show All {n}" reveal so the
// marketing content below the section stays reachable.
const pageSize = 8;

export interface PreviousDesignsOptions {
    /** The page's builder product — entries are filtered to it */
    lineItemID: string;
}

/**
 * Wires the `product-customizer/previous-designs` shortcode: loads the
 * customer's saved designs (signed in) merged with this browser's purchase
 * records, and owns every state of the section — signed-out invites, empty,
 * grid, quiet failure. The section stays hidden entirely only while loading
 * ahead of its first state. #9942.
 */
export async function SetupPreviousDesigns(opts: PreviousDesignsOptions): Promise<void> {
    const $section = document.getElementById('previous-designs');
    if (!$section) {
        return;
    }

    const noun = $section.dataset.noun || 'Coin';
    const nouns = $section.dataset.nouns || 'Coins';
    const productName = $section.dataset.product || `Custom Challenge ${nouns}`;

    const $loading = $section.querySelector('.pd-loading') as HTMLElement;
    const $error = $section.querySelector('.pd-error') as HTMLElement;
    const $empty = $section.querySelector('.pd-empty') as HTMLElement;
    const $invite = $section.querySelector('.pd-invite') as HTMLElement;
    const $inviteMessage = $invite.querySelector('.pd-invite-message') as HTMLElement;
    const $signIn = $invite.querySelector('.pd-signin') as HTMLAnchorElement;
    const $grid = $section.querySelector('.pd-grid') as HTMLElement;
    const $more = $section.querySelector('.pd-more') as HTMLElement;
    const $moreButton = $more.querySelector('button') as HTMLButtonElement;
    const $cardTemplate = $section.querySelector('.pd-card-template') as HTMLTemplateElement;

    $signIn.href = `${Relref('/login')}?return=${encodeURIComponent(window.location.href)}`;

    const user = await session.Session().then(s => s.user).catch(() => undefined);

    $section.classList.remove('d-none');
    $loading.classList.remove('d-none');

    let entries: PreviousDesignEntry[];
    try {
        entries = await LoadPreviousDesigns({ lineItemID: opts.lineItemID, loggedIn: Boolean(user) });
    } catch (err) {
        console.error(err);
        $loading.classList.add('d-none');
        $error.classList.remove('d-none');
        return;
    }
    $loading.classList.add('d-none');

    if (!entries.length) {
        if (user) {
            $empty.classList.remove('d-none');
        } else {
            $invite.classList.remove('d-none');
        }
        return;
    }

    for (const entry of entries) {
        $grid.appendChild(drawEntry(entry));
    }
    $grid.classList.remove('d-none');

    // Signed out with browser purchases: the purchases lead, the invitation
    // follows the grid and acknowledges what's shown.
    if (!user) {
        $inviteMessage.textContent = `Purchases made on this browser appear below; sign in to see your other ${nouns.toLowerCase()}.`;
        $invite.classList.remove('d-none');
        $grid.after($invite);
    }

    const $cols = Array.from($grid.children) as HTMLElement[];
    if ($cols.length > pageSize) {
        for (const $col of $cols.slice(pageSize)) {
            $col.classList.add('d-none');
        }
        ($more.querySelector('.pd-more-count') as HTMLElement).textContent = String($cols.length);
        $more.classList.remove('d-none');
        $moreButton.addEventListener('click', () => {
            for (const $col of $cols) {
                $col.classList.remove('d-none');
            }
            $more.classList.add('d-none');
        });
    }

    function drawEntry(entry: PreviousDesignEntry): HTMLElement {
        const $col = template.Clone($cardTemplate).firstElementChild as HTMLElement;
        const $card = $col.querySelector('.card') as HTMLElement;

        ($col.querySelector('.pd-name') as HTMLElement).textContent = productName;

        const $img = $col.querySelector('.pd-preview-img') as HTMLImageElement;
        const $missing = $col.querySelector('.pd-preview-missing') as HTMLElement;
        if (entry.previewURL) {
            $img.classList.remove('d-none');
            void Picture($img, entry.previewURL, { displayWidth: 300, alt: `${productName} preview` });
        } else {
            $missing.classList.remove('d-none');
            $missing.classList.add('d-flex');
        }

        if (entry.purchasedAt) {
            ($col.querySelector('.pd-badge') as HTMLElement).classList.remove('d-none');
        }

        const $meta = $col.querySelector('.pd-meta') as HTMLElement;
        const metaLines: string[] = [];
        if (entry.savedAt) {
            metaLines.push(`Saved ${DateTime.fromMillis(entry.savedAt).toLocaleString(DateTime.DATE_MED)}`);
        }
        if (entry.purchasedAt) {
            metaLines.push(`Purchased ${DateTime.fromMillis(entry.purchasedAt).toLocaleString(DateTime.DATE_MED)}`);
            metaLines.push(`Quantity ${Number(entry.quantity).toLocaleString()}`);
        }
        for (const line of metaLines) {
            const $line = document.createElement('div');
            $line.textContent = line;
            $meta.appendChild($line);
        }

        const $reopen = $col.querySelector('.pd-reopen') as HTMLButtonElement;
        $reopen.addEventListener('click', () => {
            new Modal({
                title: `Replace this ${noun.toLowerCase()}?`,
                text: `The ${noun.toLowerCase()} on the canvas is replaced once this one loads.`,
                size: 'modal-sm',
                confirmText: `Open This ${noun}`,
                cancelText: 'Keep Working',
                confirmButtonClass: 'btn btn-primary text-white btn-lg col-12 col-md-auto flex-grow-1',
                cancelButtonClass: 'btn btn-outline-secondary btn-lg col-12 col-md-auto flex-grow-1',
                onSubmit: () => {
                    $reopen.disabled = true;
                    $card.classList.add('opacity-75');
                    window.location.href = `${window.location.pathname}${entry.reopenQuery}`;
                },
            });
        });

        ($col.querySelector('.pd-share') as HTMLButtonElement).addEventListener('click', async () => {
            try {
                let key = entry.designKey;
                if (!key) {
                    const design = await BuilderDesignByOrderLineItem(entry.orderKey, entry.orderLineItemID);
                    key = design.key;
                }
                clipboard.Copy(`${window.location.origin}${window.location.pathname}?design=${encodeURIComponent(key)}`);
                toast.Success('Share link copied.');
            } catch (err) {
                console.error(err);
                toast.Error('Could not copy the share link.');
            }
        });

        // Remove is owner-only: a browser purchase record seen signed out has
        // nothing on the account to delete.
        const $removeItem = $col.querySelector('.pd-remove-item') as HTMLElement;
        if (!user || !entry.designKey) {
            $removeItem.remove();
        } else {
            let version = 0;
            const del = () => {
                $col.classList.add('d-none');
                DeleteBuilderDesign(entry.builderDesignID).then(deleted => {
                    version = deleted.version;
                    toast.Undo('Cleared from your designs.', restore);
                }).catch(err => {
                    console.error(err);
                    $col.classList.remove('d-none');
                    toast.Error('Could not remove this design.');
                });
            };
            const restore = () => {
                RestoreBuilderDesign(entry.builderDesignID, version).then(restored => {
                    version = restored.version;
                    $col.classList.remove('d-none');
                }).catch(err => {
                    console.error(err);
                    toast.Error('Could not restore this design.');
                });
            };
            ($col.querySelector('.pd-remove') as HTMLButtonElement).addEventListener('click', del);
        }

        const $orderLink = $col.querySelector('.pd-order-link') as HTMLAnchorElement;
        if (entry.orderKey) {
            $orderLink.href = `${Relref('/orders/track')}?id=${encodeURIComponent(entry.orderKey)}`;
            $orderLink.classList.remove('d-none');
        }

        return $col;
    }
}
